import { ImageQuality } from '@pcp/core/interfaces';

/**
 * Backing-store pixel multiplier per image quality, over the logical 1280×720 canvas.
 * Matches the actual slide variant resolutions (verified against blob storage):
 * low = 1280×720 (1×), med = 1920×1080 (1.5×), high = 3840×2160 (3×).
 */
const QUALITY_PIXEL_RATIO: Partial<Record<ImageQuality, number>> = {
    [ImageQuality.LOW]: 1,
    [ImageQuality.MEDIUM]: 1.5,
    [ImageQuality.HIGH]: 3,
};

/** Upper bound for the backing-store multiplier (4k over the 1280×720 logical canvas). */
export const MAX_SLIDE_CANVAS_PIXEL_RATIO = 3;

/**
 * The slide canvas's backing-store multiplier: `min(3, max(qualityMultiplier, devicePixelRatio))`.
 *
 * - The **quality term** carries the detail the client chose to download — a `high` client
 *   rasterises at 4k so the 4k source is actually visible on a large display.
 * - The **`devicePixelRatio` floor** guarantees no client renders softer than fabric's default
 *   retina behavior did before this change (e.g. a `low` teacher on a dpr-2 retina display
 *   keeps its 2× backing store).
 * - The **cap** bounds memory at the 4k buffer (≈33 MB per canvas element).
 *
 * `quality` undefined (no preference provided) degrades to the dpr floor — fabric's default.
 * Computed once at canvas creation; a mid-session quality change updates the displayed variant
 * URL immediately but the backing store only at the next canvas mount (design D2).
 */
export function slideCanvasPixelRatio(quality: ImageQuality | undefined, devicePixelRatio: number): number {
    const qualityRatio = (quality && QUALITY_PIXEL_RATIO[quality]) || 1;
    const dpr = devicePixelRatio > 0 ? devicePixelRatio : 1;
    return Math.min(MAX_SLIDE_CANVAS_PIXEL_RATIO, Math.max(qualityRatio, dpr));
}
